import { Divider, MenuItem, Select, TextField, Typography } from "@mui/material"
import { useFormContext } from "../../../hooks/forms/useFormContext"
import { 
    ACTION_FOR_RESUMING_RANDOM_MODE_TEXTS,
    ACTION_FOR_STARTING_RANDOM_MODE_TEXTS,
    ACTIONS_FOR_RESUMING_RANDOM_MODE,
    ACTIONS_FOR_STARTING_RANDOM_MODE,
    ALBUM_LIST_PAGE_SIZE_TEXTS,
    ALWAYS_CLEAR_QUEUE_TEXTS, 
    AUTO_NEW_RANDOM_ALBUM_TEXTS, 
    RANDOM_METHOD_TEXTS, 
    RANDOM_METHODS, 
    RANDOM_QUEUE_LENGTH_TEXTS, 
    RANDOM_QUEUE_MAX_LENGTH, 
    RANDOM_QUEUE_MIN_LENGTH 
} from "../../../utils/constants/userSettingsConstants"
import YesNoSelect from "../../common/other/YesNoSelect"
import { isNaturalNumber } from "../../../utils/validationUtils"
import { InfoOutlined } from "@mui/icons-material"
import { useViewport } from "../../../hooks/others/useViewport"
import { 
    SETTINGS_WINDOW_BODY_LAYOUT_TITLE_CLASS as layoutTitleFormClassValues,
    SETTINGS_WINDOW_BODY_LAYOUT_INFO_CLASS as layoutFormInfoClassValues,
    SETTINGS_WINDOW_BODY_EMPTY_SPACE_WHEN_NO_INFO as emptySpaceWhenNoInfoValues 
} from "../../../utils/constants/viewConstants"

// #region Sections

const SectionTemplate = ({title, description, formField, optionInfo}) => {
    const layoutTitleFormClass = useViewport(layoutTitleFormClassValues);
    const layoutFormInfoClass = useViewport(layoutFormInfoClassValues);
    const emptySpaceWhenNoInfo = useViewport(emptySpaceWhenNoInfoValues);

    return (
        <div className="d-flex flex-column gap-0_5">
            <div>
                <Typography variant="h6">{title}</Typography>
            </div>
            <div className={`d-flex ${layoutTitleFormClass}`}>
                <div className="flex-2">
                    {description}
                </div>
                <div className={`flex-3 d-flex ${layoutFormInfoClass} ai-center`}>
                    <div className="flex-1 full-width">
                        {formField}
                    </div>
                    {
                        (emptySpaceWhenNoInfo || optionInfo) &&
                        <div className="flex-1">
                            {optionInfo}
                        </div>
                    }
                </div>
            </div>
        </div>
    )
}

const OptionInfo = ({settingsKey, infoTexts}) => {
    const { data } = useFormContext();

    return (
        <Typography variant="body2"><InfoOutlined fontSize="inherit" style={{ verticalAlign: 'text-top'}}/> {infoTexts[data[settingsKey]].description}</Typography>
    )
}

const AlbumListPageSizeSection = () => {
    const Body = () => {
        const { data, setData, errors, setErrors } = useFormContext();

        const checkError = (value, setError) => {
            if(!isNaturalNumber(value))
            {
                setError(true)
                return;
            }

            setError(false);
        }

        const checkDetailedAlbumSizeError = value => {
            const setError = error => setErrors({...errors, detailedAlbumListPageSize: error});

            checkError(value, setError);
        }

        const checkSquaredAlbumSizeError = value => {
            const setError = error => setErrors({...errors, squaredAlbumListPageSize: error});

            checkError(value, setError);
        }

        const onChangeDetailedAlbumSize = (e) => {
            setData({...data, detailedAlbumListPageSize: e.target.value});
            checkDetailedAlbumSizeError(e.target.value);
        }

        const onChangeSquaredAlbumSize = (e) => {
            setData({...data, detailedAlbumListPageSize: e.target.value});
            checkSquaredAlbumSizeError(e.target.value);
        }

        return (
            <div className="d-flex flex-column gap-1">
                <TextField 
                    fullWidth
                    value={data.detailedAlbumListPageSize} 
                    error={errors.detailedAlbumListPageSize} 
                    onChange={onChangeDetailedAlbumSize}
                    label={ALBUM_LIST_PAGE_SIZE_TEXTS.detailedAlbumSize.label}
                    helperText={ALBUM_LIST_PAGE_SIZE_TEXTS.detailedAlbumSize.helperText} />
                <TextField 
                    fullWidth
                    value={data.squaredAlbumListPageSize} 
                    error={errors.squaredAlbumListPageSize} 
                    onChange={onChangeSquaredAlbumSize}
                    label={ALBUM_LIST_PAGE_SIZE_TEXTS.squaredAlbumSize.label}
                    helperText={ALBUM_LIST_PAGE_SIZE_TEXTS.squaredAlbumSize.helperText} />
            </div>
        )
    }

    return (
        <SectionTemplate
            title={ALBUM_LIST_PAGE_SIZE_TEXTS.title}
            description={ALBUM_LIST_PAGE_SIZE_TEXTS.description}
            formField={<Body />}
            />
    )
}

const RandomMethodSection = () => {
    const FormField = () => {
        const { data, setData } = useFormContext();

        const onChange = (e) => {
            setData({...data, randomMethod: e.target.value});
        }

        return (
            <Select fullWidth value={data.randomMethod} onChange={onChange}>
                {
                    Object.keys(RANDOM_METHODS).map((key) => <MenuItem key={key} value={key}>{RANDOM_METHOD_TEXTS[key].name}</MenuItem>)
                }
            </Select>
        )
    }

    return (
        <SectionTemplate
            title={RANDOM_METHOD_TEXTS.title}
            description={RANDOM_METHOD_TEXTS.description}
            formField={<FormField />}
            optionInfo={<OptionInfo settingsKey={'randomMethod'} infoTexts={RANDOM_METHOD_TEXTS} />}
            />
    )
}

const RandomQueueLimitSection = () => {
    const Body = () => {
        const { data, setData, errors, setErrors } = useFormContext();

        const checkError = (value) => {
            const setError = error => setErrors({...errors, randomQueueLimit: error});

            if(!isNaturalNumber(value))
            {
                setError(true)
                return;
            }

            if(+value < RANDOM_QUEUE_MIN_LENGTH || +value > RANDOM_QUEUE_MAX_LENGTH)
            {
                setError(true);
                return;
            }

            setError(false);
        }

        const onChange = (e) => {
            setData({...data, randomQueueLimit: e.target.value});
            checkError(e.target.value);
        }

        return (
            <TextField 
                fullWidth
                value={data.randomQueueLimit} 
                error={errors.randomQueueLimit} 
                onChange={onChange}
                helperText={RANDOM_QUEUE_LENGTH_TEXTS.helperText} />
        )
    }

    return (
        <SectionTemplate
            title={RANDOM_QUEUE_LENGTH_TEXTS.title}
            description={RANDOM_QUEUE_LENGTH_TEXTS.description}
            formField={<Body />}
            />
    )
}

const AutoNewRandomAlbumSection = () => {
    const FormField = () => {
        const { data, setData } = useFormContext();

        const onChange = (e) => {
            setData({...data, autoNewRandomAlbum: e.target.value});
        }

        return (
            <YesNoSelect fullWidth value={data.autoNewRandomAlbum} onChange={onChange} />
        )
    }   

    return (
        <SectionTemplate
            title={AUTO_NEW_RANDOM_ALBUM_TEXTS.title}
            description={AUTO_NEW_RANDOM_ALBUM_TEXTS.description}
            formField={<FormField />}
            optionInfo={<OptionInfo settingsKey='autoNewRandomAlbum' infoTexts={AUTO_NEW_RANDOM_ALBUM_TEXTS} />}
            />
    )
}

const AlwaysClearQueueSection = () => {
    const FormField = () => {
        const {data, setData} = useFormContext();
        
        const onChange = (e) => {
            setData({...data, alwaysClearQueue: e.target.value});
        }

        return (
            <YesNoSelect fullWidth value={data.alwaysClearQueue} onChange={onChange} notSetOption={true} />
        )
    }

    return (
        <SectionTemplate
            title={ALWAYS_CLEAR_QUEUE_TEXTS.title}
            description={ALWAYS_CLEAR_QUEUE_TEXTS.description}
            formField={<FormField />}
            optionInfo={<OptionInfo settingsKey='alwaysClearQueue' infoTexts={ALWAYS_CLEAR_QUEUE_TEXTS} />}
            />
    )
}

const ActionForStartingRandomMode = () => {
    const FormField = () => {
        const {data, setData} = useFormContext();

        const onChange = (e) => {
            setData({...data, actionForStartingRandomMode: e.target.value});
        }

        return (
            <Select fullWidth value={data.actionForStartingRandomMode} onChange={onChange}>
                {
                    Object.keys(ACTIONS_FOR_STARTING_RANDOM_MODE).map((key) => <MenuItem key={key} value={key}>{ACTION_FOR_STARTING_RANDOM_MODE_TEXTS[key].name}</MenuItem>)
                }
            </Select>
        )
    }

    return (
        <SectionTemplate 
            title={ACTION_FOR_STARTING_RANDOM_MODE_TEXTS.title}
            description={ACTION_FOR_STARTING_RANDOM_MODE_TEXTS.description}
            formField={<FormField />}
            optionInfo={<OptionInfo settingsKey={'actionForStartingRandomMode'} infoTexts={ACTION_FOR_STARTING_RANDOM_MODE_TEXTS} />}
        />
    )
}

const ActionForResumingRandomMode = () => {
    const FormField = () => {
        const {data, setData} = useFormContext();

        const onChange = (e) => {
            setData({...data, actionForResumingRandomMode: e.target.value});
        }

        return (
            <Select fullWidth value={data.actionForResumingRandomMode} onChange={onChange}>
                {
                    Object.keys(ACTIONS_FOR_RESUMING_RANDOM_MODE).map((key) => <MenuItem key={key} value={key}>{ACTION_FOR_RESUMING_RANDOM_MODE_TEXTS[key].name}</MenuItem>)
                }
            </Select>
        )
    }

    return (
        <SectionTemplate 
            title={ACTION_FOR_RESUMING_RANDOM_MODE_TEXTS.title}
            description={ACTION_FOR_RESUMING_RANDOM_MODE_TEXTS.description}
            formField={<FormField />}
            optionInfo={<OptionInfo settingsKey={'actionForResumingRandomMode'} infoTexts={ACTION_FOR_RESUMING_RANDOM_MODE_TEXTS} />}
        />
    )
}

// #endregion Sections

const SettingsFormBody = () => {
    return (
        <div className="flex-1 overflow-auto d-flex flex-column gap-0_75 pr-1">
            <AlbumListPageSizeSection />
            <Divider />
            <AlwaysClearQueueSection />
            <Divider/>
            <RandomMethodSection />
            <Divider/>
            <RandomQueueLimitSection />
            <Divider/>
            <AutoNewRandomAlbumSection />
            <Divider/>
            <ActionForStartingRandomMode />
            <Divider/>
            <ActionForResumingRandomMode />
        </div>
    )
}

export default SettingsFormBody;